import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";
import { TrendingUp, Building2, Users, Layers, FileText, Activity, Calendar } from "lucide-react";

const TAB_ITEMS = [
    { id: "analytics", label: "Cross-Workspace Analytics" },
    { id: "quarterly", label: "Quarterly Loop" },
    { id: "billing", label: "Billing & Tiers" },
];

export default function AdminPlatformPage() {
    const [tab, setTab] = useState("analytics");
    return (
        <div className="p-8 max-w-6xl space-y-6" data-testid="admin-platform-page">
            <div>
                <h1 className="text-2xl font-display font-bold text-white">Platform</h1>
                <p className="text-sm text-gray-500 mt-1 font-body">Cross-workspace analytics, quarterly reassessment, billing & tiers.</p>
            </div>

            <div className="flex items-center gap-1 border-b border-border/40">
                {TAB_ITEMS.map((t) => (
                    <button key={t.id} onClick={() => setTab(t.id)}
                        className={`px-4 py-2 -mb-px text-sm border-b-2 transition-colors ${tab === t.id ? "text-white border-white" : "text-gray-500 border-transparent hover:text-white"}`}
                        data-testid={`platform-tab-${t.id}`}>
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "analytics" && <AnalyticsTab />}
            {tab === "quarterly" && <QuarterlyTab />}
            {tab === "billing" && <BillingTab />}
        </div>
    );
}

// ---- Analytics ------------------------------------------------------------

function AnalyticsTab() {
    const { api } = useAuth();
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/v2/admin/platform/analytics").then((r) => setData(r.data)).catch(() => {});
    }, [api]);

    if (!data) return <p className="text-xs text-gray-500">Loading…</p>;

    const totals = data.totals || {};
    const avgs = data.averages || {};
    const verdicts = data.verdict_distribution || {};
    const segments = data.segment_distribution || {};
    const industry = data.industry_distribution || {};
    const tiers = data.tier_distribution || {};

    const KPI = ({ icon: Icon, label, value }) => (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border/40 p-5">
            <Icon className="w-4 h-4 text-gray-600 mb-3" />
            <p className="text-2xl font-bold text-white font-mono">{value}</p>
            <p className="text-xs text-gray-500 mt-1">{label}</p>
        </motion.div>
    );

    return (
        <div className="space-y-6" data-testid="platform-analytics-tab">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KPI icon={Building2} label="Workspaces" value={totals.workspaces ?? 0} />
                <KPI icon={Users} label="Candidates" value={totals.candidates ?? 0} />
                <KPI icon={Layers} label="Cycles" value={totals.cycles ?? 0} />
                <KPI icon={FileText} label="Verdicts" value={totals.verdicts ?? 0} />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KPI icon={TrendingUp} label="Avg Capability" value={(avgs.capability ?? 0).toFixed(1)} />
                <KPI icon={TrendingUp} label="Avg Displacement" value={(avgs.displacement ?? 0).toFixed(1)} />
                <KPI icon={Users} label="Users" value={totals.users ?? 0} />
                <KPI icon={Activity} label="Enrollments" value={totals.enrollments ?? 0} />
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <Bucket title="Verdict Distribution" data={verdicts} colors={{ Retain: "#34D399", Retrain: "#FBBF24", Redeploy: "#F87171" }} />
                <Bucket title="Capability Segments" data={segments} />
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <Bucket title="Industries" data={industry} />
                <Bucket title="Subscription Tiers" data={tiers} />
            </div>

            <div className="bg-card border border-border/40" data-testid="platform-workspaces-table">
                <p className="px-4 py-2 text-xs text-gray-500 uppercase tracking-wider border-b border-border/40">Workspaces ({data.workspaces.length})</p>
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-gray-500 uppercase tracking-wider border-b border-border/40">
                            <th className="text-left px-4 py-2">Name</th>
                            <th className="text-left px-4 py-2">Industry</th>
                            <th className="text-left px-4 py-2">Tier</th>
                            <th className="text-right px-4 py-2">Candidates</th>
                            <th className="text-right px-4 py-2">Cycles</th>
                            <th className="text-right px-4 py-2">Verdicts</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.workspaces.map((w) => (
                            <tr key={w.id} className="border-b border-border/20" data-testid={`platform-ws-${w.id}`}>
                                <td className="px-4 py-2 text-white">{w.name}</td>
                                <td className="px-4 py-2 text-gray-400">{w.primary_industry || "—"}</td>
                                <td className="px-4 py-2 text-gray-400 font-mono uppercase">{w.subscription_tier || "standard"}</td>
                                <td className="px-4 py-2 text-right font-mono text-white">{w.candidates}</td>
                                <td className="px-4 py-2 text-right font-mono text-white">{w.cycles}</td>
                                <td className="px-4 py-2 text-right font-mono text-white">{w.verdicts}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Bucket({ title, data, colors }) {
    const entries = Object.entries(data || {});
    const total = entries.reduce((s, [, n]) => s + n, 0);
    return (
        <div className="bg-card border border-border/40 p-6">
            <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-3">{title}</h3>
            <div className="space-y-2">
                {entries.length === 0 && <p className="text-xs text-gray-600">No data.</p>}
                {entries.map(([k, n]) => {
                    const pct = total ? Math.round(100 * n / total) : 0;
                    const color = colors?.[k] || "#FFFFFF";
                    return (
                        <div key={k}>
                            <div className="flex items-center justify-between text-xs mb-1">
                                <span className="text-gray-400">{k}</span>
                                <span className="font-mono text-white">{n} ({pct}%)</span>
                            </div>
                            <div className="h-1.5 bg-black border border-border/40">
                                <div className="h-full" style={{ width: `${pct}%`, background: color }} />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ---- Quarterly ------------------------------------------------------------

function QuarterlyTab() {
    const { api } = useAuth();
    const [list, setList] = useState([]);
    const [busy, setBusy] = useState({});
    const [msg, setMsg] = useState("");

    const load = useCallback(() => {
        api.get("/v2/admin/platform/quarterly/due").then((r) => setList(r.data.workspaces || [])).catch(() => {});
    }, [api]);

    useEffect(() => { load(); }, [load]);

    const reassess = async (lastCycleId, wsName) => {
        if (!lastCycleId) { setMsg(`No prior cycle for ${wsName} — create one in /admin/scoring first.`); return; }
        setBusy({ ...busy, [lastCycleId]: true }); setMsg("");
        try {
            const r = await api.post("/v2/admin/platform/quarterly/reassess", { source_cycle_id: lastCycleId });
            setMsg(`Reassessment cycle provisioned for ${wsName}: "${r.data.name}". Open it in /admin/scoring.`);
            load();
        } catch (e) {
            setMsg(e?.response?.data?.detail || e.message);
        } finally {
            setBusy({ ...busy, [lastCycleId]: false });
        }
    };

    return (
        <div className="space-y-4" data-testid="platform-quarterly-tab">
            <div className="bg-card border border-border/40 px-4 py-3 text-xs text-gray-400 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-600" />
                Workspaces are flagged as due when their most recent locked or active cycle is older than the configured quarterly cadence (default 3 months).
            </div>
            {msg && <p className="text-xs text-amber-300">{msg}</p>}

            <div className="bg-card border border-border/40">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-gray-500 uppercase tracking-wider border-b border-border/40">
                            <th className="text-left px-4 py-2">Workspace</th>
                            <th className="text-left px-4 py-2">Last Cycle</th>
                            <th className="text-right px-4 py-2">Months Elapsed</th>
                            <th className="text-right px-4 py-2">Cadence (months)</th>
                            <th className="text-left px-4 py-2">Status</th>
                            <th className="text-right px-4 py-2">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((row) => (
                            <tr key={row.workspace_id} className="border-b border-border/20" data-testid={`quarterly-row-${row.workspace_id}`}>
                                <td className="px-4 py-2 text-white">{row.workspace_name}</td>
                                <td className="px-4 py-2 text-gray-400">{row.last_cycle?.name || "—"}</td>
                                <td className="px-4 py-2 text-right font-mono text-white">{row.months_elapsed != null ? row.months_elapsed : "—"}</td>
                                <td className="px-4 py-2 text-right font-mono text-gray-400">{row.cadence_months}</td>
                                <td className="px-4 py-2">
                                    {row.due ? <span className="px-2 py-0.5 text-xs font-mono uppercase border border-amber-500/40 text-amber-300">Due</span>
                                            : <span className="px-2 py-0.5 text-xs font-mono uppercase border border-emerald-500/40 text-emerald-300">Current</span>}
                                </td>
                                <td className="px-4 py-2 text-right">
                                    <button onClick={() => reassess(row.last_cycle?.id, row.workspace_name)}
                                        disabled={!row.last_cycle?.id || !!busy[row.last_cycle?.id]}
                                        className="px-2 py-1 text-xs border border-white/20 text-white hover:bg-white/5 disabled:opacity-40"
                                        data-testid={`reassess-${row.workspace_id}`}>
                                        {busy[row.last_cycle?.id] ? "Provisioning…" : "Provision next cycle"}
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {list.length === 0 && <tr><td colSpan="6" className="px-4 py-6 text-center text-gray-600">No workspaces yet.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ---- Billing -------------------------------------------------------------

const TIERS = ["standard", "growth", "enterprise"];

function BillingTab() {
    const { api } = useAuth();
    const [workspaces, setWorkspaces] = useState([]);
    const [enterpriseId, setEnterpriseId] = useState("");
    const [data, setData] = useState(null);
    const [tierConfig, setTierConfig] = useState(null);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");

    useEffect(() => {
        api.get("/v2/admin/workspaces").then((r) => {
            const ws = r.data.workspaces || r.data || [];
            setWorkspaces(ws);
            if (ws.length && !enterpriseId) setEnterpriseId(ws[0].id);
        }).catch(() => {});
        api.get("/v2/admin/platform/tier-limits").then((r) => setTierConfig(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = useCallback(() => {
        if (!enterpriseId) return;
        api.get(`/v2/admin/platform/billing/${enterpriseId}`).then((r) => setData(r.data)).catch(() => {});
    }, [api, enterpriseId]);

    useEffect(() => { load(); }, [load]);

    const setTier = async (tier) => {
        setBusy(true); setMsg("");
        try {
            await api.patch(`/v2/admin/platform/billing/${enterpriseId}/tier`, { subscription_tier: tier });
            setMsg(`Tier updated to ${tier}.`);
            load();
        } catch (e) {
            setMsg(e?.response?.data?.detail || e.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="space-y-4" data-testid="platform-billing-tab">
            <div className="bg-card border border-border/40 px-4 py-3 flex flex-wrap items-center gap-3">
                <span className="text-xs text-gray-500 uppercase tracking-wider">Workspace</span>
                <select value={enterpriseId} onChange={(e) => setEnterpriseId(e.target.value)}
                    className="bg-black border border-border/40 text-white text-sm px-3 py-1.5"
                    data-testid="billing-workspace-select">
                    {workspaces.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
            </div>
            {msg && <p className="text-xs text-amber-300">{msg}</p>}

            {data && (
                <>
                    <div className="bg-card border border-border/40 p-6 flex items-center justify-between gap-4 flex-wrap" data-testid="billing-tier-card">
                        <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wider">Current Tier</p>
                            <p className="text-2xl font-display font-bold text-white">{data.workspace.tier_label}</p>
                            <p className="text-xs text-gray-500 mt-1 font-mono">{data.workspace.subscription_tier}</p>
                        </div>
                        <div className="flex items-center gap-2">
                            {(tierConfig?.tier_order || TIERS).map((t) => (
                                <button key={t} onClick={() => setTier(t)} disabled={busy || data.workspace.subscription_tier === t}
                                    className={`px-3 py-1.5 text-xs border ${data.workspace.subscription_tier === t ? "border-white text-white" : "border-border/40 text-gray-400 hover:text-white hover:border-white/30"} disabled:opacity-40`}
                                    data-testid={`set-tier-${t}`}>
                                    {t}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="billing-usage">
                        {[
                            { l: "Candidates", v: data.usage.candidates, max: data.tier_limits.max_candidates, pct: data.headroom.candidates_pct_used },
                            { l: "Cycles This Quarter", v: data.usage.cycles_this_quarter, max: data.tier_limits.max_cycles_per_quarter, pct: data.headroom.cycles_this_quarter_pct_used },
                            { l: "Verdicts", v: data.usage.verdicts, max: null, pct: null },
                            { l: "Enrollments", v: data.usage.enrollments, max: null, pct: null },
                        ].map((s) => {
                            const over = s.pct != null && s.pct >= 100;
                            return (
                                <div key={s.l} className={`bg-card border ${over ? "border-red-500/40" : "border-border/40"} p-5`}>
                                    <p className="text-xs text-gray-500 uppercase tracking-wider">{s.l}</p>
                                    <p className={`text-2xl font-mono font-bold mt-1 ${over ? "text-red-300" : "text-white"}`}>{s.v}</p>
                                    {s.max != null && (
                                        <p className="text-xs text-gray-500 mt-1 font-mono">/ {s.max} ({s.pct}%)</p>
                                    )}
                                    {over && <p className="text-xs text-red-300 mt-1">Over plan limit</p>}
                                </div>
                            );
                        })}
                    </div>

                    <div className="bg-card border border-border/40 p-6" data-testid="billing-secondary-usage">
                        <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-3">Other Usage</h3>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                            {[
                                { l: "Total Cycles", v: data.usage.cycles_total },
                                { l: "Assessment Instances", v: data.usage.assessment_instances },
                                { l: "Scoring Jobs", v: data.usage.scoring_jobs },
                            ].map((s) => (
                                <div key={s.l} className="border border-border/40 px-3 py-2">
                                    <p className="text-gray-500 uppercase tracking-wider">{s.l}</p>
                                    <p className="font-mono text-white text-lg">{s.v}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-card border border-border/40 p-6" data-testid="billing-tier-table">
                        <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-3">Tier Limits</h3>
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-gray-500 uppercase tracking-wider border-b border-border/40">
                                    <th className="text-left py-2">Tier</th>
                                    <th className="text-right py-2">Max Candidates</th>
                                    <th className="text-right py-2">Cycles/Quarter</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(tierConfig?.tier_order || TIERS).map((t) => {
                                    const cfg = tierConfig?.tiers?.[t] || {};
                                    return (
                                        <tr key={t} className="border-b border-border/20 last:border-0">
                                            <td className="py-2 text-white">{cfg.label || t}</td>
                                            <td className="text-right font-mono">{cfg.max_candidates ?? "—"}</td>
                                            <td className="text-right font-mono">{cfg.max_cycles_per_quarter ?? "—"}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}
